import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../../services/auth.service';
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from '../../../../common/common';
import { PlatformViewModel } from '../../../../model/platform.model';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { NgxPaginationModule } from 'ngx-pagination';
import { FormsModule } from '@angular/forms';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {LoaderComponent} from "../../../common/loader/loader.component";
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-platform',
  standalone: true,
  imports: [RouterModule, CommonModule, NgxPaginationModule, FormsModule, LoaderComponent],
  templateUrl: './platform-index.component.html',
  styleUrl: './platform-index.component.scss'
})
export class PlatformIndexComponent implements OnInit, OnDestroy {
  // Search fields
  public idSearch: string = '';
  public amqpHostSearch: string = '';
  public xEnergyEnabledSearch: string = '';
  public vensCountSearch: string = '';
  public updatedBySearch: string = '';

  // Debounce search using BehaviorSubject
  private searchParams$ = new BehaviorSubject<any>({
    id: '',
    amqpHost: '',
    xEnergyEnabled: '',
    vensCount: '',
    updatedBy: ''
  });
  private unsubscribe = new Subject<void>();

  protected readonly paginationId = 'platformsPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  public isAdmin: boolean = false;
  public isLoading: boolean = false;
  public displayedPlatform: PlatformViewModel[] = [];
  public error: string | null = null;
  public router: Router;

  constructor(private injector: Injector, private authService: AuthService, private platformService: ApiService) {
    this.router = injector.get(Router);
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search that tracks actual parameter changes
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.loadPlatforms();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depth': 2,
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    // Add search parameters if they exist
    if (this.idSearch) {
      params['environment'] = this.idSearch.trim(); // Platform ID is stored as 'environment'
    }
    if (this.amqpHostSearch) {
      params['amqp_host'] = this.amqpHostSearch.trim();
    }
    if (this.xEnergyEnabledSearch) {
      params['x_energy_enabled'] = this.xEnergyEnabledSearch.trim();
    }
    if (this.updatedBySearch) {
      params['updated_by'] = this.updatedBySearch.trim();
    }
    // Note: vensCount will be client-side filtered since it's a computed value

    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();

    // Update the search parameters subject with current values
    this.searchParams$.next({
      id: this.idSearch,
      amqpHost: this.amqpHostSearch,
      xEnergyEnabled: this.xEnergyEnabledSearch,
      vensCount: this.vensCountSearch,
      updatedBy: this.updatedBySearch
    });
  }

  loadPlatforms() {
    this.isLoading = true;
    this.error = null;

    // Build parameters using the centralized method
    const queryParams = this.buildSearchParams();

    this.platformService.getPlatform(queryParams)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.displayedPlatform = response.map((platform: PlatformViewModel) => platform);

          // Apply client-side filtering for computed fields
          this.displayedPlatform = this.applyClientSideFilters(this.displayedPlatform);

          this.paginationModel = this.pagination.updateTotalItems(this.displayedPlatform.length);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading platforms:', err);
          this.error = extractErrorMessage(err, "Failed to load platforms.");
          this.isLoading = false;
        }
      });
  }

  private applyClientSideFilters(platforms: PlatformViewModel[]): PlatformViewModel[] {
    let filteredPlatforms = platforms;

    // Filter by vens count if search term exists
    if (this.vensCountSearch) {
      const searchTerm = this.vensCountSearch.trim();
      filteredPlatforms = filteredPlatforms.filter(platform =>
        platform.vens && platform.vens.length.toString().includes(searchTerm)
      );
    }

    return filteredPlatforms;
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.loadPlatforms();
  }

  nextPage(event: any, url: string, router: Router) {
    return goToNextPage(event, url, router);
  }

  delete(platform: any) {
    if (confirm('Are you sure you want to delete this platform?')) {
      this.platformService.deletePlatform(platform.environment)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: () => {
            this.loadPlatforms(); // Reload platforms after successful delete
          },
          error: (err) => {
            console.error('Error deleting platform:', err);
            this.error = extractErrorMessage(err, "Failed to delete platform.");
          }
        });
    }
  }
}
